import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './context/AuthContext';
import { FeatureProvider, useAlleenBeheer } from './context/FeatureContext';
import { StudentAuthProvider } from './context/StudentAuthContext';
import { AccessibilityProvider } from './context/AccessibilityContext';
import ProtectedRoute from './components/ProtectedRoute';
import FeatureRoute from './components/FeatureRoute';
import RoleRoute from './components/RoleRoute';
import LesRoute from './components/LesRoute';
import StudentProtectedRoute from './components/StudentProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';
import TestEnvBadge from './components/TestEnvBadge';
import ScrollToTop from './components/ScrollToTop';
import VoorgerenderdeFallback from './components/VoorgerenderdeFallback';
import './App.css';

// Alleen de landingspagina eager (snelste eerste paint); al het overige
// marketing-/auth-scherm lazy zodat het niet in de main-chunk zit die élke
// ingelogde docent/leerling op de iPad meedownloadt. react-snap pre-rendert
// deze routes nog steeds (wacht op de chunk), dus geen SEO-verlies.
import HomeHero from './pages/HomeHero';
import ExamentrainingOefenen from './pages/ExamentrainingOefenen';
const ExamentrainingLogin = lazy(() => import('./pages/ExamentrainingLogin'));
const ExamentrainingBeheer = lazy(() => import('./pages/ExamentrainingBeheer'));
const ExamentrainingOpdrachten = lazy(() => import('./pages/ExamentrainingOpdrachten'));
const ExamentrainingOpdrachtSpeler = lazy(() => import('./pages/ExamentrainingOpdrachtSpeler'));

const DeviceCheck = lazy(() => import('./pages/DeviceCheck'));
const Contact = lazy(() => import('./pages/Contact'));
const LoginNew = lazy(() => import('./pages/LoginNew'));
const OfferteNew = lazy(() => import('./pages/OfferteNew'));
const Privacy = lazy(() => import('./pages/Privacy'));
const Voorwaarden = lazy(() => import('./pages/Voorwaarden'));
const Subverwerkers = lazy(() => import('./pages/Subverwerkers'));
const FAQ = lazy(() => import('./pages/FAQ'));
const HoeWerkt = lazy(() => import('./pages/HoeWerkt'));
const Download = lazy(() => import('./pages/Download'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogArtikel = lazy(() => import('./pages/BlogArtikel'));
const VakPaginaPubliek = lazy(() => import('./pages/VakPaginaPubliek'));
const KennisbankAuteur = lazy(() => import('./pages/KennisbankAuteur'));
const OverOns = lazy(() => import('./pages/OverOns'));
const NotFound = lazy(() => import('./pages/NotFound'));

// Auth pages (lazy - login is nooit de eerste paint)
const StudentLogin = lazy(() => import('./pages/StudentLogin'));
const EntreeCallback = lazy(() => import('./pages/EntreeCallback'));
const TestStudentLogin = lazy(() => import('./pages/TestStudentLogin'));
const StudentLoginTest = lazy(() => import('./pages/StudentLoginTest'));
const StudentSupabaseLogin = lazy(() => import('./pages/StudentSupabaseLogin'));

// Protected pages (lazy loaded - alleen na login, bespaart ~60% bundle)
const DashboardNew = lazy(() => import('./pages/DashboardNew'));
const BeheerderDashboard = lazy(() => import('./pages/BeheerderDashboard'));
// TestDashboard verwijderd (was oude monolithische dashboard, vervangen door DashboardNew)
const ToetsenPage = lazy(() => import('./pages/ToetsenPage'));
const CommunicatiePage = lazy(() => import('./pages/CommunicatiePage'));
const ResultatenPage = lazy(() => import('./pages/ResultatenPage'));
const AgendaPage = lazy(() => import('./pages/AgendaPage'));
const CoachingDashboard = lazy(() => import('./pages/coaching/CoachingDashboard'));
const CoachgesprekDetail = lazy(() => import('./pages/coaching/CoachgesprekDetail'));
const CoachingBeheer = lazy(() => import('./pages/coaching/CoachingBeheer'));
const CoachProfiel = lazy(() => import('./pages/coaching/CoachProfiel'));
const LeerlingProfiel = lazy(() => import('./pages/coaching/LeerlingProfiel'));
const KlassenPage = lazy(() => import('./pages/KlassenPage'));
const SchoolbeheerPage = lazy(() => import('./pages/SchoolbeheerPage'));
const StatistiekenToetsPage = lazy(() => import('./pages/StatistiekenToetsPage'));
const WerkenBij = lazy(() => import('./pages/WerkenBij'));
const Vacature = lazy(() => import('./pages/Vacature'));
const OpenSollicitatie = lazy(() => import('./pages/OpenSollicitatie'));
const StatistiekenCommunicatiePage = lazy(() => import('./pages/StatistiekenCommunicatiePage'));
const NieuweToetsPage = lazy(() => import('./pages/NieuweToetsPage'));
const SnelleSessiePage = lazy(() => import('./pages/SnelleSessiePage'));
const MonitorPage = lazy(() => import('./pages/MonitorPage'));
const PreviewPage = lazy(() => import('./pages/PreviewPage'));
const BewerkenPage = lazy(() => import('./pages/BewerkenPage'));
const ResultatenToetsPage = lazy(() => import('./pages/ResultatenToetsPage'));
const InstellingenPage = lazy(() => import('./pages/InstellingenPage'));
const HelpFaqPage = lazy(() => import('./pages/HelpFaqPage'));
const FeedbackPage = lazy(() => import('./pages/FeedbackPage'));
const LeerdoelenBankPage = lazy(() => import('./pages/LeerdoelenBankPage'));
const LeerkaartenPage = lazy(() => import('./pages/LeerkaartenPage'));
const VragenBankPage = lazy(() => import('./pages/VragenBankPage'));
const Boekenplank = lazy(() => import('./pages/Boekenplank'));
const LesmodusPage = lazy(() => import('./pages/LesmodusPage'));
const MijnLeerlingenPage = lazy(() => import('./pages/MijnLeerlingenPage'));
const LeerlingDetailPage = lazy(() => import('./pages/LeerlingDetailPage'));
const ToegankelijkheidTestPage = lazy(() => import('./pages/ToegankelijkheidTestPage'));

// Lazy-loaded pages (loaded on demand to reduce initial bundle)
// NOTE: Oude docent pagina's (DocentDashboard, ToetsAanmaken, ToetsBewerken, LiveMonitor,
// ToetsResultaten, Statistieken, ClassManagement) zijn vervangen door nieuwe versies.
// Bestanden staan nog in src/pages/ als backup.
const LeerlingRooster = lazy(() => import('./pages/LeerlingRooster'));
const CommunicatieStart = lazy(() => import('./pages/CommunicatieStart'));
const ToetsMaken = lazy(() => import('./pages/ToetsMaken'));
const ToetsBedankt = lazy(() => import('./pages/ToetsBedankt'));
const StudentDashboard = lazy(() => import('./pages/StudentDashboard'));
const StudentAvailableTests = lazy(() => import('./pages/StudentAvailableTests'));
const StudentCompletedTests = lazy(() => import('./pages/StudentCompletedTests'));
const StudentCommunicatie = lazy(() => import('./pages/StudentCommunicatie'));
const StudentCommunicatieVak = lazy(() => import('./pages/StudentCommunicatieVak'));
const StudentCoaching = lazy(() => import('./pages/StudentCoaching'));
const StudentIntake = lazy(() => import('./pages/StudentIntake'));
const StudentGesprekDetail = lazy(() => import('./pages/StudentGesprekDetail'));
const StudentLeerstof = lazy(() => import('./pages/StudentLeerstof'));
const StudentLes = lazy(() => import('./pages/StudentLes'));
const StudentVoortgang = lazy(() => import('./pages/StudentVoortgang'));
const StudentAgenda = lazy(() => import('./pages/StudentAgenda'));
const StudentInstellingen = lazy(() => import('./pages/StudentInstellingen'));
const StudentHelp = lazy(() => import('./pages/StudentHelp'));
const StudentLeerkaarten = lazy(() => import('./pages/StudentLeerkaarten'));
const LeerkaartenSpeler = lazy(() => import('./pages/LeerkaartenSpeler'));
const InzageModule = lazy(() => import('./pages/InzageModule'));
const CommunicatieResultaat = lazy(() => import('./pages/CommunicatieResultaat'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const DesignPreview = lazy(() => import('./pages/DesignPreview'));

// Portal-setter — zet data-portal op <html> op basis van route prefix
// zodat design-system tokens (--brand-primary etc.) per portaal switchen
function PortalSetter() {
  const { pathname } = useLocation();
  useEffect(() => {
    const html = document.documentElement;
    if (
      pathname.startsWith('/leerling') ||
      pathname.startsWith('/toets') ||
      pathname.startsWith('/student') ||
      pathname.startsWith('/inzage') ||
      // vóór de docent-check: '/communicatie-resultaat' zou anders op het
      // '/communicatie'-prefix van het docentportaal matchen
      pathname.startsWith('/communicatie-resultaat') ||
      pathname.startsWith('/communicatie-start')
    ) {
      html.setAttribute('data-portal', 'leerling');
    } else if (
      pathname.startsWith('/dashboard') ||
      pathname.startsWith('/toetsen-page') ||
      pathname.startsWith('/communicatie') ||
      pathname.startsWith('/resultaten') ||
      pathname.startsWith('/agenda') ||
      pathname.startsWith('/klassen') ||
      pathname.startsWith('/statistieken') ||
      pathname.startsWith('/nieuwe-toets') ||
      pathname.startsWith('/bewerken') ||
      pathname.startsWith('/monitor') ||
      pathname.startsWith('/instellingen') ||
      pathname.startsWith('/vragen-bank') ||
      pathname.startsWith('/leerdoelen') ||
      pathname.startsWith('/leerkaarten') ||
      pathname.startsWith('/mijn-leerlingen') ||
      pathname.startsWith('/toegankelijkheid-test') ||
      pathname.startsWith('/admin')
    ) {
      html.setAttribute('data-portal', 'docent');
    } else {
      html.setAttribute('data-portal', 'public');
    }
  }, [pathname]);
  return null;
}

// Loading fallback for lazy-loaded pages
function PageLoader() {
  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    }}>
      <div style={{
        width: '40px',
        height: '40px',
        border: '3px solid rgba(255,255,255,0.1)',
        borderTopColor: '#0D9488',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
      }} />
    </div>
  );
}

// Redirect component die URL params doormapt naar querystring op de target route.
// Bv: <RedirectWithParams to="/resultaten-toets" mapping={{ id: 'testId' }} />
// → /docent/resultaten/abc → /resultaten-toets?id=abc
function RedirectWithParams({ to, mapping }) {
  const params = useParams();
  const qs = new URLSearchParams();
  Object.entries(mapping || {}).forEach(([queryKey, paramKey]) => {
    const val = params[paramKey];
    if (val) qs.set(queryKey, val);
  });
  const queryString = qs.toString();
  return <Navigate to={`${to}${queryString ? `?${queryString}` : ''}`} replace />;
}

// Zelfde pad, ander dashboard: een beheerder zonder lestaak (migratie 074)
// ziet zijn beheerdersdashboard, iedereen anders het docentdashboard.
function DashboardKeuze() {
  const alleenBeheer = useAlleenBeheer();
  return alleenBeheer ? <BeheerderDashboard /> : <DashboardNew />;
}

function AppContent() {
  // Meld de iOS-exam-app dat de UI gerenderd is → native verbergt de laad-overlay pas dán
  // (i.p.v. zodra de version-check klaar is) → geen homepage-flash tijdens React-boot.
  useEffect(() => {
    const signalReady = () => { try { window.sendToNativeApp?.({ action: 'app_ready' }); } catch (e) {} };
    // Dubbele rAF: minstens één frame gepaint vóór we 'klaar' melden.
    const id = requestAnimationFrame(() => requestAnimationFrame(signalReady));
    return () => cancelAnimationFrame(id);
  }, []);

  return (
    <div className="App">
      <PortalSetter />
      <ScrollToTop />
      <TestEnvBadge />
      {/* Op een voorgerenderde pagina blijft de HTML staan tot de code er is (geen knipperen). */}
      <Suspense fallback={<VoorgerenderdeFallback><PageLoader /></VoorgerenderdeFallback>}>
      <Routes>
        {/* ============================================ */}
        {/* PUBLIEKE ROUTES - Marketing & Auth */}
        {/* ============================================ */}
        <Route path="/" element={<HomeHero />} />
        <Route path="/features" element={<Navigate to="/hoe-werkt-het" replace />} />
        <Route path="/check" element={<DeviceCheck />} />
        <Route path="/download" element={<Download />} />
        {/* Beheerder zonder lestaak (074) krijgt zijn eigen dashboard op hetzelfde pad. */}
        <Route path="/dashboard-new" element={<ProtectedRoute><DashboardKeuze /></ProtectedRoute>} />
        <Route path="/test-dashboard" element={<Navigate to="/dashboard-new" replace />} />
        <Route path="/toetsen-page" element={<FeatureRoute feature="toetsen"><ProtectedRoute><ToetsenPage /></ProtectedRoute></FeatureRoute>} />
        <Route path="/communicatie-page" element={<FeatureRoute feature="communicatie"><ProtectedRoute><CommunicatiePage /></ProtectedRoute></FeatureRoute>} />
        <Route path="/resultaten-page" element={<ProtectedRoute><ResultatenPage /></ProtectedRoute>} />
        <Route path="/agenda-page" element={<FeatureRoute feature="agenda"><ProtectedRoute><AgendaPage /></ProtectedRoute></FeatureRoute>} />
        <Route path="/coaching" element={<FeatureRoute feature="coaching"><ProtectedRoute><LesRoute><CoachingDashboard /></LesRoute></ProtectedRoute></FeatureRoute>} />
        <Route path="/coaching/beheer" element={<FeatureRoute feature="coaching"><ProtectedRoute><RoleRoute roles={['schoolbeheerder', 'admin']} beheerrecht="coaching_inzien"><CoachingBeheer /></RoleRoute></ProtectedRoute></FeatureRoute>} />
        <Route path="/coaching/beheer/coach/:docentId" element={<FeatureRoute feature="coaching"><ProtectedRoute><RoleRoute roles={['schoolbeheerder', 'admin']} beheerrecht="coaching_inzien"><CoachProfiel /></RoleRoute></ProtectedRoute></FeatureRoute>} />
        <Route path="/coaching/beheer/leerling/:leerlingId" element={<FeatureRoute feature="coaching"><ProtectedRoute><RoleRoute roles={['schoolbeheerder', 'admin']} beheerrecht="coaching_inzien"><LeerlingProfiel /></RoleRoute></ProtectedRoute></FeatureRoute>} />
        <Route path="/coaching/:id" element={<FeatureRoute feature="coaching"><ProtectedRoute><CoachgesprekDetail /></ProtectedRoute></FeatureRoute>} />
        <Route path="/klassen-page" element={<ProtectedRoute><KlassenPage /></ProtectedRoute>} />
        <Route path="/schoolbeheer" element={<ProtectedRoute><RoleRoute roles={['schoolbeheerder', 'admin']}><SchoolbeheerPage /></RoleRoute></ProtectedRoute>} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/login" element={<LoginNew />} />
        <Route path="/uitnodiging/demo-2026" element={<Navigate to="/demo" replace />} />
        <Route path="/demo" element={<OfferteNew />} />
        <Route path="/offerte" element={<Navigate to="/demo" replace />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/voorwaarden" element={<Voorwaarden />} />
        <Route path="/subverwerkers" element={<Subverwerkers />} />
        <Route path="/veelgestelde-vragen" element={<FAQ />} />
        <Route path="/hoe-werkt-het" element={<HoeWerkt />} />
        <Route path="/over-ons" element={<OverOns />} />
        <Route path="/kennisbank" element={<Blog />} />
        <Route path="/kennisbank/:slug" element={<BlogArtikel />} />
        {/* Digitaal toetsen per vak (18-10-2026); inhoud in src/data/vakpaginas.js. */}
        <Route path="/digitaal-toetsen" element={<VakPaginaPubliek />} />
        <Route path="/digitaal-toetsen/:vak" element={<VakPaginaPubliek />} />
        <Route path="/kennisbank/auteur/:slug" element={<KennisbankAuteur />} />
        {/* Werken bij — publiek. open-sollicitatie staat bewust vóór :slug,
            anders vangt de slug-route hem af. */}
        <Route path="/werken-bij" element={<WerkenBij />} />
        <Route path="/werken-bij/open-sollicitatie" element={<OpenSollicitatie />} />
        <Route path="/werken-bij/:slug" element={<Vacature />} />
        {/* Engelse variant: dezelfde componenten met lang="en", dezelfde slugs. */}
        <Route path="/en/careers" element={<WerkenBij lang="en" />} />
        <Route path="/en/careers/open-application" element={<OpenSollicitatie lang="en" />} />
        <Route path="/en/careers/:slug" element={<Vacature lang="en" />} />
        {/* Engelse versie van de rest van de publieke site (27-10-2026). Paden:
            src/content/site/paden.js. Kennisbankartikelen blijven Nederlands
            (label "Article in Dutch"); de inlogflow zelf is niet vertaald. */}
        <Route path="/en" element={<HomeHero lang="en" />} />
        <Route path="/en/about" element={<OverOns lang="en" />} />
        <Route path="/en/contact" element={<Contact lang="en" />} />
        <Route path="/en/how-it-works" element={<HoeWerkt lang="en" />} />
        <Route path="/en/faq" element={<FAQ lang="en" />} />
        <Route path="/en/demo" element={<OfferteNew lang="en" />} />
        <Route path="/en/quote" element={<Navigate to="/en/demo" replace />} />
        <Route path="/en/download" element={<Download lang="en" />} />
        <Route path="/en/privacy" element={<Privacy lang="en" />} />
        <Route path="/en/terms" element={<Voorwaarden lang="en" />} />
        <Route path="/en/subprocessors" element={<Subverwerkers lang="en" />} />
        <Route path="/en/knowledge-base" element={<Blog lang="en" />} />
        <Route path="/en/knowledge-base/author/:slug" element={<KennisbankAuteur lang="en" />} />
        <Route path="/en/knowledge-base/:slug" element={<BlogArtikel lang="en" />} />
        <Route path="/en/digital-assessment" element={<VakPaginaPubliek lang="en" />} />
        <Route path="/en/digital-assessment/:vak" element={<VakPaginaPubliek lang="en" />} />
        <Route path="/en/login" element={<LoginNew lang="en" />} />
        <Route path="/statistieken-toets" element={<ProtectedRoute><LesRoute><StatistiekenToetsPage /></LesRoute></ProtectedRoute>} />
        <Route path="/statistieken-communicatie" element={<FeatureRoute feature="communicatie"><ProtectedRoute><StatistiekenCommunicatiePage /></ProtectedRoute></FeatureRoute>} />
        <Route path="/nieuwe-toets" element={<FeatureRoute feature="toetsen"><ProtectedRoute><NieuweToetsPage mode="toets" /></ProtectedRoute></FeatureRoute>} />
        <Route path="/nieuwe-communicatie" element={<FeatureRoute feature="communicatie"><ProtectedRoute><NieuweToetsPage mode="communicatie" /></ProtectedRoute></FeatureRoute>} />
        {/* Snelle sessie (14-10-2026): exit ticket of instapper op één scherm */}
        <Route path="/communicatie-snel" element={<FeatureRoute feature="communicatie"><ProtectedRoute><SnelleSessiePage /></ProtectedRoute></FeatureRoute>} />
        <Route path="/monitor" element={<ProtectedRoute><LesRoute><MonitorPage /></LesRoute></ProtectedRoute>} />
        <Route path="/preview" element={<ProtectedRoute><LesRoute><PreviewPage /></LesRoute></ProtectedRoute>} />
        <Route path="/bewerken" element={<ProtectedRoute><LesRoute><BewerkenPage /></LesRoute></ProtectedRoute>} />
        <Route path="/resultaten-toets" element={<ProtectedRoute><ResultatenToetsPage /></ProtectedRoute>} />
        <Route path="/instellingen" element={<ProtectedRoute><InstellingenPage /></ProtectedRoute>} />
        <Route path="/help-faq" element={<ProtectedRoute><HelpFaqPage /></ProtectedRoute>} />
        <Route path="/feedback" element={<ProtectedRoute><FeedbackPage /></ProtectedRoute>} />
        <Route path="/leerdoelen" element={<FeatureRoute feature="leerdoelen"><ProtectedRoute><LeerdoelenBankPage /></ProtectedRoute></FeatureRoute>} />
        {/* Leerkaarten: eigen schakelaar 'leerkaarten' (14-10-2026). */}
        <Route path="/leerkaarten" element={<FeatureRoute feature="leerkaarten"><ProtectedRoute><LeerkaartenPage /></ProtectedRoute></FeatureRoute>} />
        <Route path="/vragenbank" element={<FeatureRoute feature="vragenbank"><ProtectedRoute><VragenBankPage /></ProtectedRoute></FeatureRoute>} />
        <Route path="/boekenplank" element={<FeatureRoute feature="boekenplank"><ProtectedRoute><Boekenplank /></ProtectedRoute></FeatureRoute>} />
        <Route path="/lesmodus" element={<FeatureRoute feature="lesmodus"><ProtectedRoute><LesmodusPage /></ProtectedRoute></FeatureRoute>} />
        <Route path="/externe-toets" element={<FeatureRoute feature="lesmodus"><ProtectedRoute><LesmodusPage soort="externe_toets" /></ProtectedRoute></FeatureRoute>} />
        <Route path="/mijn-leerlingen" element={<ProtectedRoute><MijnLeerlingenPage /></ProtectedRoute>} />
        <Route path="/leerling-detail" element={<ProtectedRoute><LeerlingDetailPage /></ProtectedRoute>} />
        {/* Voorlezen en leesbaarheid testen als docent (dyslexie, 09-10-2026) */}
        <Route path="/toegankelijkheid-test" element={<ProtectedRoute><ToegankelijkheidTestPage /></ProtectedRoute>} />

        {/* ============================================ */}
        {/* LEGACY LEERLING ROUTES - Code-based */}
        {/* Geen navigatie, alleen via iOS app of directe link */}
        {/* ============================================ */}
        {/* /leerling en /leerling-start → redirect naar nieuwe 2-stap student-login */}
        <Route path="/leerling" element={<Navigate to="/student-login" replace />} />
        <Route path="/leerling-start" element={<Navigate to="/student-login" replace />} />
        <Route path="/leerling-rooster" element={<LeerlingRooster />} />
        <Route path="/communicatie-start" element={<CommunicatieStart />} />
        <Route path="/inzage" element={<InzageModule />} />
        <Route path="/inzage/:code" element={<InzageModule />} />
        <Route path="/toets/:sessionId" element={<AccessibilityProvider><ToetsMaken /></AccessibilityProvider>} />
        <Route path="/toets-bedankt" element={<ToetsBedankt />} />

        {/* ============================================ */}
        {/* NIEUWE STUDENT ROUTES - Entree-based */}
        {/* Geauthenticeerde leerlingen via Kennisnet Entree */}
        {/* ============================================ */}
        <Route path="/student" element={<Navigate to="/student-login" replace />} />
        <Route path="/student-login" element={<StudentLogin />} />
        <Route path="/test-student-login" element={<TestStudentLogin />} />
        <Route path="/student-login-test" element={<StudentLoginTest />} />
        <Route path="/student-login-supabase" element={<StudentSupabaseLogin />} />
        <Route path="/auth/entree-callback" element={<EntreeCallback />} />
        <Route path="/auth/entree-test-callback" element={<EntreeCallback />} />
        <Route
          path="/student-dashboard"
          element={
            <StudentProtectedRoute>
              <StudentDashboard />
            </StudentProtectedRoute>
          }
        />
        <Route
          path="/student/toetsen"
          element={
            <StudentProtectedRoute>
              <StudentAvailableTests />
            </StudentProtectedRoute>
          }
        />
        <Route
          path="/student/resultaten"
          element={
            <StudentProtectedRoute>
              <StudentCompletedTests />
            </StudentProtectedRoute>
          }
        />
        <Route
          path="/student/communicatie"
          element={
            <StudentProtectedRoute>
              <StudentCommunicatie />
            </StudentProtectedRoute>
          }
        />
        <Route
          path="/student/communicatie/vak/:vak"
          element={
            <StudentProtectedRoute>
              <StudentCommunicatieVak />
            </StudentProtectedRoute>
          }
        />
        <Route
          path="/student/coaching"
          element={
            <StudentProtectedRoute>
              <StudentCoaching />
            </StudentProtectedRoute>
          }
        />
        <Route
          path="/student/coaching/:id/intake"
          element={
            <StudentProtectedRoute>
              <StudentIntake />
            </StudentProtectedRoute>
          }
        />
        <Route
          path="/student/coaching/:id"
          element={
            <StudentProtectedRoute>
              <StudentGesprekDetail />
            </StudentProtectedRoute>
          }
        />
        <Route
          path="/student/leerstof"
          element={
            <StudentProtectedRoute>
              <StudentLeerstof />
            </StudentProtectedRoute>
          }
        />
        {/* Leerkaartensets oefenen (14-10-2026); verborgen als school-feature 'leerkaarten' uit staat */}
        <Route
          path="/student/leerkaarten"
          element={
            <StudentProtectedRoute>
              <StudentLeerkaarten />
            </StudentProtectedRoute>
          }
        />
        <Route
          path="/student/leerkaarten/:setId"
          element={
            <StudentProtectedRoute>
              <LeerkaartenSpeler />
            </StudentProtectedRoute>
          }
        />
        <Route
          path="/student/les"
          element={
            <StudentProtectedRoute>
              <StudentLes />
            </StudentProtectedRoute>
          }
        />
        {/* Publieke les-ingang: anoniem via code + naam (geen login) */}
        <Route path="/les/:code" element={<StudentLes publiek />} />
        <Route
          path="/student/voortgang"
          element={
            <StudentProtectedRoute>
              <StudentVoortgang />
            </StudentProtectedRoute>
          }
        />
        <Route
          path="/student/agenda"
          element={
            <StudentProtectedRoute>
              <StudentAgenda />
            </StudentProtectedRoute>
          }
        />
        <Route
          path="/student/klassen"
          element={<Navigate to="/student/instellingen" replace />}
        />
        <Route
          path="/student/instellingen"
          element={
            <StudentProtectedRoute>
              <StudentInstellingen />
            </StudentProtectedRoute>
          }
        />
        <Route
          path="/student/help"
          element={
            <StudentProtectedRoute>
              <StudentHelp />
            </StudentProtectedRoute>
          }
        />
        <Route
          path="/student/inzage"
          element={
            <StudentProtectedRoute>
              <InzageModule />
            </StudentProtectedRoute>
          }
        />
        <Route
          path="/student/inzage/:code"
          element={
            <StudentProtectedRoute>
              <InzageModule />
            </StudentProtectedRoute>
          }
        />
        <Route
          path="/communicatie-resultaat/:sessionId"
          element={<CommunicatieResultaat />}
        />

        {/* ============================================ */}
        {/* LEGACY DOCENT ROUTES - Redirects naar nieuwe pagina's */}
        {/* Oude bestanden staan nog in src/pages/ als backup */}
        {/* ============================================ */}
        <Route path="/docent-dashboard" element={<Navigate to="/dashboard-new" replace />} />
        <Route path="/toets-aanmaken" element={<Navigate to="/nieuwe-toets" replace />} />
        <Route path="/toets-bewerken/:testId" element={<Navigate to="/bewerken" replace />} />
        <Route path="/docent/monitor/:testId" element={<Navigate to="/monitor" replace />} />
        <Route path="/docent/resultaten/:testId" element={<RedirectWithParams to="/resultaten-toets" mapping={{ id: 'testId' }} />} />
        <Route path="/docent/nakijken/:testId/:sessionId" element={<RedirectWithParams to="/resultaten-toets" mapping={{ id: 'testId' }} />} />
        <Route path="/docent/statistieken/:testId" element={<Navigate to="/statistieken-toets" replace />} />
        <Route path="/docent/statistieken" element={<Navigate to="/statistieken-toets" replace />} />
        <Route path="/docent/klassen" element={<Navigate to="/klassen-page" replace />} />

        {/* ============================================ */}
        {/* ADMIN ROUTE - Alleen voor admin accounts */}
        {/* ============================================ */}
        {/* Geen ProtectedRoute: die stuurt naar de docent-login, waardoor het
            eigen beheer-inlogscherm onbereikbaar was. AdminDashboard doet zijn
            eigen, strengere check (alleen admin@testwisely.nl) en de backend
            eist bovendien een admin-token op elk endpoint. */}
        <Route path="/admin" element={<AdminDashboard />} />

        {/* Design-system preview (intern, geen SEO) */}
        <Route path="/design-preview" element={<DesignPreview />} />

        {/* Examentraining (exam.testwisely.nl-module) */}
        <Route path="/examentraining-demo" element={<ExamentrainingOefenen />} />
        <Route path="/examentraining-login" element={<ExamentrainingLogin />} />
        <Route path="/examentraining-beheer" element={<ExamentrainingBeheer />} />
        <Route path="/examentraining" element={<ExamentrainingOpdrachten />} />
        <Route path="/examentraining-opdracht/:opdrachtId" element={<ExamentrainingOpdrachtSpeler />} />

        {/* 404, moet altijd als laatste route staan */}
        <Route path="*" element={<NotFound />} />
      </Routes>
      </Suspense>
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <Router>
          <AuthProvider>
            <FeatureProvider>
              <StudentAuthProvider>
                <AppContent />
              </StudentAuthProvider>
            </FeatureProvider>
          </AuthProvider>
        </Router>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
